import { useSortable } from '@dnd-kit/react/sortable';
import { useTranslation } from 'react-i18next';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { twMerge } from 'tailwind-merge';

import {
  DraggableRectanglesIcon,
  MinusIcon,
  PlusIcon,
} from '../../../../images/shapes';

const sortableTransition = {
  duration: 300,
  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
  idle: true,
};

export interface DashboardSectionItemData {
  /**
   * Unique section identifier used by the sortable list.
   */
  id: string;
  /**
   * Position of the item within its group.
   */
  index: number;
  /**
   * Section label displayed in the sidebar.
   */
  label: string;
  /**
   * Callback toggling the section visibility.
   */
  onClick: () => void;
}

interface DashboardSectionItemProps extends DashboardSectionItemData {
  /**
   * Group the item belongs to; determines the action icon.
   */
  variant: 'active' | 'available';
}

const DashboardSectionItem = ({
  id,
  index,
  label,
  variant,
  onClick,
}: DashboardSectionItemProps) => {
  const { t } = useTranslation();
  const builtInLabels: Record<string, string> = {
    stats: t('Home.Stats'),
    'first-steps': t('Home.FirstSteps'),
    'latest-updates': t('Home.LatestUpdates'),
    'quick-links': t('Home.QuickLinks'),
  };
  const ActionIcon = variant === 'active' ? MinusIcon : PlusIcon;
  const { isDragging, ref, handleRef } = useSortable({
    id,
    index,
    group: variant,
    modifiers: [RestrictToVerticalAxis],
    transition: sortableTransition,
  });

  return (
    <div
      ref={(element) => {
        ref(element);
        handleRef(element);
      }}
      onPointerDownCapture={(event) => {
        if ((event.target as HTMLElement).closest('[data-no-drag]')) {
          event.stopPropagation();
        }
      }}
      className={twMerge(
        'flex items-center gap-2 py-2 cursor-grab',
        isDragging && 'opacity-50 contain-[layout_paint]',
      )}
    >
      <button
        type="button"
        className="flex shrink-0 cursor-grab items-center justify-center"
      >
        <DraggableRectanglesIcon className="h-4 w-auto text-slate-500 " />
      </button>
      <span
        className={twMerge([
          'min-w-0 flex-1 truncate text-base font-normal',
          variant === 'active'
            ? 'text-slate-900 dark:text-slate-200'
            : 'text-slate-500 dark:text-slate-400',
        ])}
      >
        {Object.hasOwn(builtInLabels, id) ? builtInLabels[id] : label}
      </span>
      <button
        type="button"
        className="flex h-5 w-5 shrink-0 cursor-pointer items-center
         justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
        onClick={onClick}
        data-no-drag
      >
        <ActionIcon className="h-2 w-2 text-slate-900 dark:text-white" />
      </button>
    </div>
  );
};

export default DashboardSectionItem;
